import { EffectsCommandMap } from 'dva';
import { ConnectState } from '@/models/connect';
import { FI18n, FServiceAPI } from '@freelog/tools-lib';
import fMessage from '@/components/fMessage';
import { FRegExpMgr } from '@/utils/FRegExpMgr';
import type {
  ChangeAction,
  OnChange_step1_resourceType_Action,
  OnChange_step1_resourceTitle_Action,
  OnChange_step1_resourceName_Action,
  OnVerify_step1_resourceName_Action,
  OnClick_step1_createBtn_Action,
  ResourceCreatorPageModelState,
} from './types';

export const step1Effects = {
  *onChange_step1_resourceType(
    { payload }: OnChange_step1_resourceType_Action,
    { select, put }: EffectsCommandMap,
  ) {
    const { resourceCreatorPage }: ConnectState = yield select(
      ({ resourceCreatorPage }: ConnectState) => ({
        resourceCreatorPage,
      }),
    );
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step1_resourceType: payload.value,
        step1_dataIsDirty_count: resourceCreatorPage.step1_dataIsDirty_count + 1,
        step1_resourceType_errorText:
          payload.value === null
            ? FI18n.i18nNext.t('naming_convention_resource_type_required')
            : '',
      },
    });
  },
  *onChange_step1_resourceTitle(
    { payload }: OnChange_step1_resourceTitle_Action,
    { select, put }: EffectsCommandMap,
  ) {
    const { resourceCreatorPage }: ConnectState = yield select(
      ({ resourceCreatorPage }: ConnectState) => ({
        resourceCreatorPage,
      }),
    );
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step1_resourceTitle: payload.value,
        step1_resourceTitle_errorText:
          payload.value === ''
            ? FI18n.i18nNext.t('naming_convention_resource_title_required')
            : payload.value.length > 100
            ? '不超过100个字符'
            : '',
      },
    });
  },
  *onChange_step1_resourceName(
    { payload }: OnChange_step1_resourceName_Action,
    { select, put }: EffectsCommandMap,
  ) {
    const { resourceCreatorPage }: ConnectState = yield select(
      ({ resourceCreatorPage }: ConnectState) => ({
        resourceCreatorPage,
      }),
    );
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step1_resourceName: payload.value,
        step1_resourceName_optimized: FRegExpMgr.resourceNameOptimized(payload.value),
        step1_resourceName_errorText: '',
        step1_resourceName_isVerify: true,
        step1_dataIsDirty_count: resourceCreatorPage.step1_dataIsDirty_count + 1,
      },
    });
  },
  *onVerify_step1_resourceName(
    {}: OnVerify_step1_resourceName_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { resourceCreatorPage }: ConnectState = yield select(
      ({ resourceCreatorPage }: ConnectState) => ({
        resourceCreatorPage,
      }),
    );
    let nameErrorText: string = '';
    if (resourceCreatorPage.step1_resourceName_optimized === '') {
      nameErrorText = FI18n.i18nNext.t('naming_convention_resource_authid_required');
    } else {
      const params1: Parameters<typeof FServiceAPI.Resource.info>[0] = {
        resourceIdOrName: encodeURIComponent(
          `${resourceCreatorPage.userInfo?.userName}/${resourceCreatorPage.step1_resourceName_optimized}`,
        ),
      };
      const { data: data_info } = yield call(FServiceAPI.Resource.info, params1);
      if (!!data_info) {
        nameErrorText = FI18n.i18nNext.t('resource_name_exist', {
          authID: resourceCreatorPage.step1_resourceName_optimized,
        });
      }
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step1_resourceName_isVerify: false,
        step1_resourceName_errorText: nameErrorText,
      },
    });
  },
  *onClick_step1_createBtn(
    {}: OnClick_step1_createBtn_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { resourceCreatorPage } = yield select(({ resourceCreatorPage }: ConnectState) => ({
      resourceCreatorPage,
    }));
    if (resourceCreatorPage.step1_resourceType === null) {
      return;
    }
    const params: Parameters<typeof FServiceAPI.Resource.create>[0] = {
      name: resourceCreatorPage.step1_resourceName_optimized,
      resourceTypeCode: resourceCreatorPage.step1_resourceType.value,
      resourceTypeName: resourceCreatorPage.step1_resourceType.customInput || undefined,
      resourceTitle: resourceCreatorPage.step1_resourceTitle.trim(),
    };
    const {
      ret,
      errCode,
      msg,
      data,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        resourceId: string;
        resourceName: string;
        resourceType: string[];
        resourceTypeCode: string;
      };
    } = yield call(FServiceAPI.Resource.create, params);
    if (ret !== 0 || errCode !== 0 || !data) {
      self._czc?.push(['_trackEvent', '资源创建页', '创建资源', '', 0]);
      fMessage(msg, 'error');
      return;
    }
    self._czc?.push(['_trackEvent', '资源创建页', '创建资源', '', 1]);

    const params1: Parameters<typeof FServiceAPI.Resource.getResourceTypeInfoByCode>[0] = {
      code: resourceCreatorPage.step1_resourceType.value,
    };
    const {
      data: data_ResourceTypeInfo,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        resourceConfig: {
          fileCommitMode: number[];
          fileMaxSize: number;
          fileMaxSizeUnit: 1 | 2;
          supportDownload: 1 | 2;
          supportEdit: 1 | 2;
          supportOptionalConfig: 1 | 2;
        };
      };
    } = yield call(FServiceAPI.Resource.getResourceTypeInfoByCode, params1);
    const uploadEntry: ResourceCreatorPageModelState['step2_resourceTypeConfig']['uploadEntry'] =
      [];
    if (data_ResourceTypeInfo.resourceConfig.fileCommitMode.includes(2 ** 0)) {
      uploadEntry.push('localUpload');
    }
    if (data_ResourceTypeInfo.resourceConfig.fileCommitMode.includes(2 ** 1)) {
      uploadEntry.push('storageSpace');
    }
    if (data_ResourceTypeInfo.resourceConfig.fileCommitMode.includes(2 ** 2)) {
      uploadEntry.push('markdownEditor');
    }
    if (data_ResourceTypeInfo.resourceConfig.fileCommitMode.includes(2 ** 3)) {
      uploadEntry.push('cartoonEditor');
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        step: 2,
        step1_createdResourceInfo: {
          resourceID: data.resourceId,
          resourceName: data.resourceName,
          resourceType: data.resourceType,
          resourceTypeCode: data.resourceTypeCode,
        },
        step1_dataIsDirty_count: 0,
        step2_resourceTypeConfig: {
          uploadEntry: uploadEntry,
          limitFileSize:
            data_ResourceTypeInfo.resourceConfig.fileMaxSize *
            1024 *
            1024 ** data_ResourceTypeInfo.resourceConfig.fileMaxSizeUnit,
          isSupportDownload: data_ResourceTypeInfo.resourceConfig.supportDownload === 2,
          isSupportEdit: data_ResourceTypeInfo.resourceConfig.supportEdit === 2,
          isSupportOptionalConfig:
            data_ResourceTypeInfo.resourceConfig.supportOptionalConfig === 2,
        },
        step1_resourceTitle: resourceCreatorPage.step1_resourceName,
      },
    });
  },
};
